import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';

import { clamp, clampCropArea, getPendingImagePreviewFilter, getPendingImageWarmthOverlay } from './cropUtils';
import { CropAreaPixels, PendingImageItem } from './pendingUploadTypes';

interface Props {
  disabled?: boolean;
  item: PendingImageItem;
  onCropChange: (localId: string, nextCropArea: CropAreaPixels, nextZoom: number) => void;
}

const MIN_CROP_SIZE = 16;
const FRAME_BORDER_WIDTH = 2;
const CORNER_HANDLE_HIT_SIZE = 28;
const CORNER_HANDLE_VISIBLE_SIZE = 14;
const EDGE_HANDLE_HIT_LENGTH = 40;
const EDGE_HANDLE_HIT_THICKNESS = 20;
const EDGE_HANDLE_VISIBLE_LENGTH = 20;
const EDGE_HANDLE_VISIBLE_THICKNESS = 4;

type ResizeHandle = 'n' | 'e' | 's' | 'w' | 'nw' | 'ne' | 'sw' | 'se';
type FixedResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

type DragOrigin =
  | {
      area: CropAreaPixels;
      mode: 'move';
      x: number;
      y: number;
    }
  | {
      area: CropAreaPixels;
      handle: ResizeHandle;
      mode: 'resize';
    };

function getMinCropWidth(dimensions: PendingImageItem['dimensions']) {
  return Math.min(MIN_CROP_SIZE, dimensions.width);
}

function getMinCropHeight(dimensions: PendingImageItem['dimensions']) {
  return Math.min(MIN_CROP_SIZE, dimensions.height);
}

function getFixedResizeAnchor(area: CropAreaPixels, handle: FixedResizeHandle) {
  const right = area.x + area.width;
  const bottom = area.y + area.height;

  switch (handle) {
    case 'nw':
      return {
        anchorX: right,
        anchorY: bottom,
      };
    case 'ne':
      return {
        anchorX: area.x,
        anchorY: bottom,
      };
    case 'sw':
      return {
        anchorX: right,
        anchorY: area.y,
      };
    case 'se':
    default:
      return {
        anchorX: area.x,
        anchorY: area.y,
      };
  }
}

function getFixedResizedCropArea(params: {
  area: CropAreaPixels;
  aspectRatio: number;
  dimensions: PendingImageItem['dimensions'];
  handle: FixedResizeHandle;
  pointerX: number;
  pointerY: number;
}) {
  const { area, aspectRatio, dimensions, handle, pointerX, pointerY } = params;
  const { anchorX, anchorY } = getFixedResizeAnchor(area, handle);
  const requestedWidthFromX = Math.abs(pointerX - anchorX);
  const requestedWidthFromY = Math.abs(pointerY - anchorY) * aspectRatio;
  const maxWidthByHorizontalSpace = handle.includes('w') ? anchorX : dimensions.width - anchorX;
  const maxHeightByVerticalSpace = handle.includes('n') ? anchorY : dimensions.height - anchorY;
  const maxWidth = Math.max(Math.min(maxWidthByHorizontalSpace, maxHeightByVerticalSpace * aspectRatio), 1);
  const width = clamp(
    Math.min(requestedWidthFromX, requestedWidthFromY),
    Math.min(getMinCropWidth(dimensions), maxWidth),
    maxWidth,
  );
  const height = width / aspectRatio;

  switch (handle) {
    case 'nw':
      return {
        x: anchorX - width,
        y: anchorY - height,
        width,
        height,
      };
    case 'ne':
      return {
        x: anchorX,
        y: anchorY - height,
        width,
        height,
      };
    case 'sw':
      return {
        x: anchorX - width,
        y: anchorY,
        width,
        height,
      };
    case 'se':
    default:
      return {
        x: anchorX,
        y: anchorY,
        width,
        height,
      };
  }
}

function getFreeformResizedCropArea(params: {
  area: CropAreaPixels;
  dimensions: PendingImageItem['dimensions'];
  handle: ResizeHandle;
  pointerX: number;
  pointerY: number;
}) {
  const { area, dimensions, handle, pointerX, pointerY } = params;
  const minWidth = getMinCropWidth(dimensions);
  const minHeight = getMinCropHeight(dimensions);
  let nextLeft = area.x;
  let nextTop = area.y;
  let nextRight = area.x + area.width;
  let nextBottom = area.y + area.height;

  if (handle.includes('w')) {
    nextLeft = clamp(pointerX, 0, nextRight - minWidth);
  }

  if (handle.includes('e')) {
    nextRight = clamp(pointerX, nextLeft + minWidth, dimensions.width);
  }

  if (handle.includes('n')) {
    nextTop = clamp(pointerY, 0, nextBottom - minHeight);
  }

  if (handle.includes('s')) {
    nextBottom = clamp(pointerY, nextTop + minHeight, dimensions.height);
  }

  return clampCropArea(
    {
      x: nextLeft,
      y: nextTop,
      width: nextRight - nextLeft,
      height: nextBottom - nextTop,
    },
    dimensions,
  );
}

function getResizeCursor(handle: ResizeHandle) {
  switch (handle) {
    case 'n':
    case 's':
      return 'ns-resize';
    case 'e':
    case 'w':
      return 'ew-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'nw':
    case 'se':
    default:
      return 'nwse-resize';
  }
}

function getHandlePositionStyles(params: { handle: ResizeHandle; isEdgeHandle: boolean }) {
  const { handle, isEdgeHandle } = params;
  const isTop = handle.includes('n');
  const isBottom = handle.includes('s');
  const isLeft = handle.includes('w');
  const isRight = handle.includes('e');

  return {
    top: isTop
      ? isEdgeHandle
        ? -(EDGE_HANDLE_HIT_THICKNESS / 2)
        : -(CORNER_HANDLE_HIT_SIZE / 2)
      : handle === 'e' || handle === 'w'
      ? '50%'
      : undefined,
    bottom: isBottom ? (isEdgeHandle ? -(EDGE_HANDLE_HIT_THICKNESS / 2) : -(CORNER_HANDLE_HIT_SIZE / 2)) : undefined,
    left: isLeft
      ? isEdgeHandle
        ? -(EDGE_HANDLE_HIT_THICKNESS / 2)
        : -(CORNER_HANDLE_HIT_SIZE / 2)
      : handle === 'n' || handle === 's'
      ? '50%'
      : undefined,
    right: isRight ? (isEdgeHandle ? -(EDGE_HANDLE_HIT_THICKNESS / 2) : -(CORNER_HANDLE_HIT_SIZE / 2)) : undefined,
    transform:
      handle === 'n' || handle === 's'
        ? 'translateX(-50%)'
        : handle === 'e' || handle === 'w'
        ? 'translateY(-50%)'
        : undefined,
  };
}

export default function PendingImageCropEditor({ disabled = false, item, onCropChange }: Props) {
  const imageFrameRef = useRef<HTMLDivElement | null>(null);
  const dragOriginRef = useRef<DragOrigin | null>(null);
  const dragListenerCleanupRef = useRef<(() => void) | null>(null);
  const latestInteractionRef = useRef({ disabled, item, onCropChange, scale: 0 });
  const [frameWidth, setFrameWidth] = useState(0);

  useEffect(() => {
    if (!imageFrameRef.current) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setFrameWidth(entry.contentRect.width);
    });

    observer.observe(imageFrameRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const frameHeight = useMemo(() => {
    if (!frameWidth || !item.dimensions.width || !item.dimensions.height) {
      return 0;
    }

    return frameWidth * (item.dimensions.height / item.dimensions.width);
  }, [frameWidth, item.dimensions.height, item.dimensions.width]);

  const scale = useMemo(() => {
    if (!frameWidth || !item.dimensions.width) {
      return 0;
    }

    return frameWidth / item.dimensions.width;
  }, [frameWidth, item.dimensions.width]);

  const cropOverlay = useMemo(() => {
    if (!scale) {
      return null;
    }

    return {
      left: item.croppedAreaPixels.x * scale,
      top: item.croppedAreaPixels.y * scale,
      width: item.croppedAreaPixels.width * scale,
      height: item.croppedAreaPixels.height * scale,
    };
  }, [
    item.croppedAreaPixels.height,
    item.croppedAreaPixels.width,
    item.croppedAreaPixels.x,
    item.croppedAreaPixels.y,
    scale,
  ]);

  const resizeHandles = useMemo(() => {
    if (item.cropMode === 'fixed') {
      return ['nw', 'ne', 'sw', 'se'] as const;
    }

    return ['n', 'e', 's', 'w', 'nw', 'ne', 'sw', 'se'] as const;
  }, [item.cropMode]);

  latestInteractionRef.current = { disabled, item, onCropChange, scale };

  const clearActiveDrag = () => {
    dragOriginRef.current = null;
    dragListenerCleanupRef.current?.();
    dragListenerCleanupRef.current = null;
  };

  useEffect(() => {
    if (!disabled) {
      return;
    }

    clearActiveDrag();
  }, [disabled]);

  useEffect(
    () => () => {
      clearActiveDrag();
    },
    [],
  );

  const warmthOverlay = getPendingImageWarmthOverlay(item.adjustments);

  const attachDragListeners = () => {
    if (dragListenerCleanupRef.current) {
      return;
    }
    const handlePointerMove = (event: PointerEvent) => {
      const dragOrigin = dragOriginRef.current;
      const {
        disabled: isDisabled,
        item: latestItem,
        onCropChange: latestOnCropChange,
        scale: latestScale,
      } = latestInteractionRef.current;

      if (!dragOrigin || !latestScale || isDisabled) {
        return;
      }

      if (dragOrigin.mode === 'move') {
        const deltaX = (event.clientX - dragOrigin.x) / latestScale;
        const deltaY = (event.clientY - dragOrigin.y) / latestScale;

        latestOnCropChange(
          latestItem.localId,
          clampCropArea(
            {
              ...dragOrigin.area,
              x: dragOrigin.area.x + deltaX,
              y: dragOrigin.area.y + deltaY,
            },
            latestItem.dimensions,
          ),
          latestItem.zoom,
        );
        return;
      }

      const frameRect = imageFrameRef.current?.getBoundingClientRect();

      if (!frameRect) {
        return;
      }

      const pointerX = clamp((event.clientX - frameRect.left) / latestScale, 0, latestItem.dimensions.width);
      const pointerY = clamp((event.clientY - frameRect.top) / latestScale, 0, latestItem.dimensions.height);

      const nextCropArea =
        latestItem.cropMode === 'fixed'
          ? getFixedResizedCropArea({
              area: dragOrigin.area,
              aspectRatio: latestItem.fixedAspectRatio,
              dimensions: latestItem.dimensions,
              handle: dragOrigin.handle as FixedResizeHandle,
              pointerX,
              pointerY,
            })
          : getFreeformResizedCropArea({
              area: dragOrigin.area,
              dimensions: latestItem.dimensions,
              handle: dragOrigin.handle,
              pointerX,
              pointerY,
            });

      latestOnCropChange(latestItem.localId, nextCropArea, latestItem.zoom);
    };

    const handlePointerEnd = () => {
      clearActiveDrag();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);

    dragListenerCleanupRef.current = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  };

  const startMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    dragOriginRef.current = {
      area: item.croppedAreaPixels,
      mode: 'move',
      x: event.clientX,
      y: event.clientY,
    };
    attachDragListeners();
  };

  const startResize = (handle: ResizeHandle) => (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    dragOriginRef.current = {
      area: item.croppedAreaPixels,
      handle,
      mode: 'resize',
    };
    attachDragListeners();
  };

  return (
    <Box
      ref={imageFrameRef}
      sx={{
        width: '100%',
        height: frameHeight || 'auto',
        position: 'relative',
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
        borderRadius: 1,
      }}
    >
      <Box
        component="img"
        alt={item.filename}
        src={item.objectUrl}
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
          filter: getPendingImagePreviewFilter(item.adjustments),
        }}
      />

      {warmthOverlay.opacity ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: warmthOverlay.color,
            mixBlendMode: 'soft-light',
            opacity: warmthOverlay.opacity,
            pointerEvents: 'none',
          }}
        />
      ) : null}

      {cropOverlay ? (
        <Box
          aria-label="Crop frame"
          role="presentation"
          sx={{
            position: 'absolute',
            left: cropOverlay.left,
            top: cropOverlay.top,
            width: cropOverlay.width,
            height: cropOverlay.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
            border: `${FRAME_BORDER_WIDTH}px solid`,
            borderColor: 'common.white',
            cursor: disabled ? 'default' : 'grab',
            '&:active': {
              cursor: disabled ? 'default' : 'grabbing',
            },
          }}
          onPointerDown={startMove}
        >
          {resizeHandles.map((handle) => {
            const isEdgeHandle = ['n', 'e', 's', 'w'].includes(handle);
            const positionStyles = getHandlePositionStyles({
              handle,
              isEdgeHandle,
            });

            return (
              <Box
                key={handle}
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isEdgeHandle
                    ? handle === 'n' || handle === 's'
                      ? EDGE_HANDLE_HIT_LENGTH
                      : EDGE_HANDLE_HIT_THICKNESS
                    : CORNER_HANDLE_HIT_SIZE,
                  height: isEdgeHandle
                    ? handle === 'n' || handle === 's'
                      ? EDGE_HANDLE_HIT_THICKNESS
                      : EDGE_HANDLE_HIT_LENGTH
                    : CORNER_HANDLE_HIT_SIZE,
                  ...positionStyles,
                  cursor: disabled ? 'default' : getResizeCursor(handle),
                  zIndex: 1,
                }}
                onPointerDown={startResize(handle)}
              >
                <Box
                  sx={{
                    width: isEdgeHandle
                      ? handle === 'n' || handle === 's'
                        ? EDGE_HANDLE_VISIBLE_LENGTH
                        : EDGE_HANDLE_VISIBLE_THICKNESS
                      : CORNER_HANDLE_VISIBLE_SIZE,
                    height: isEdgeHandle
                      ? handle === 'n' || handle === 's'
                        ? EDGE_HANDLE_VISIBLE_THICKNESS
                        : EDGE_HANDLE_VISIBLE_LENGTH
                      : CORNER_HANDLE_VISIBLE_SIZE,
                    borderRadius: isEdgeHandle ? 999 : '50%',
                    bgcolor: 'primary.main',
                    border: '2px solid',
                    borderColor: 'common.white',
                    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
                  }}
                />
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}
